import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/lib/i18n';
import { formatSignedUsd } from '@/lib/money-format';
import { safeJsonParse } from './safe-json';
import {
  groupLabelKey, groupReceiptEntryId, receiptHref, sumEntries, type LedgerGroup,
} from './ledger-labels';

const RECENT_COUNT = 3;

/**
 * The last few payments and credits, in the balance panel, so the first
 * screen answers "did my top-up land?" without scrolling to the full records.
 *
 * Fetches its own three rows rather than sharing the records card's page:
 * the two must fail independently, and the records card below renders the
 * error with a retry. A failure here therefore hides this list instead of
 * repeating that error inside the balance panel.
 */
export function RecentRecords({ onShowAll }: { onShowAll: () => void }) {
  const { t, locale } = useI18n();
  const [groups, setGroups] = useState<LedgerGroup[] | null>(null);
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const params = new URLSearchParams({ kind: 'billing', limit: String(RECENT_COUNT) });
        const response = await fetch(`/api/wallet/ledger?${params}`, { credentials: 'include' });
        if (!response.ok) throw new Error(`ledger ${response.status}`);
        const data = await safeJsonParse<{ groups: LedgerGroup[] }>(response);
        if (!data) throw new Error('ledger payload was not JSON');
        setGroups(data.groups.slice(0, RECENT_COUNT));
      } catch (err) {
        console.error('Error fetching recent records:', err);
        setFailed(true);
      }
    })();
  }, []);

  if (failed || groups === null) return null;

  const day = (at: number) =>
    new Intl.DateTimeFormat(locale, { month: 'numeric', day: 'numeric' }).format(new Date(at));

  return (
    <div className="wallet-recent">
      <div className="wallet-recent__head">
        <span>{t('dashboard.wallet.recentRecords')}</span>
        {groups.length > 0 && (
          <button type="button" className="wallet-recent__all" onClick={onShowAll}>
            {t('dashboard.wallet.allRecords')}
          </button>
        )}
      </div>
      {groups.length === 0 ? (
        <p className="wallet-recent__empty">{t('dashboard.wallet.ledger.empty')}</p>
      ) : (
        groups.map((g) => {
          const total = sumEntries(g.entries);
          const receiptEntryId = groupReceiptEntryId(g);
          return (
            <div key={g.groupId} className="wallet-recent__row">
              <span className="wallet-recent__date">{day(g.occurredAt)}</span>
              <span className="wallet-recent__label">{t(groupLabelKey(g.kind))}</span>
              <span>
                {receiptEntryId && (
                  <a
                    className="wallet-card__receipt-link"
                    href={receiptHref(receiptEntryId)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('dashboard.wallet.ledger.viewReceipt')}
                  </a>
                )}
              </span>
              <span className={`wallet-card__ledger-amount ${total > 0 ? 'wallet-card__ledger-amount--credit' : ''}`}>
                {total > 0 ? `+${formatSignedUsd(total)}` : formatSignedUsd(total)}
              </span>
            </div>
          );
        })
      )}
    </div>
  );
}
